import React, { useState, useEffect, useMemo } from "react";
import { Check, Sparkles, Zap, Crown, CreditCard, CheckCircle2, Loader2 } from "lucide-react";
import { endpoints } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

/**
 * Settings → Billing tab. Shows 3 tiers (Starter / Pro / Premium) with a
 * Monthly/Annual toggle (10% off annual). Razorpay Checkout opens on Subscribe.
 *
 * Auto-falls-back to one-time orders when the Razorpay account doesn't have
 * Subscriptions enabled (412 from /create-subscription).
 */

const TIER_META = {
  starter: { icon: Sparkles, color: "text-slate-500",   ring: "ring-slate-200" },
  pro:     { icon: Zap,      color: "text-primary",     ring: "ring-primary/30" },
  premium: { icon: Crown,    color: "text-amber-500",   ring: "ring-amber-200" },
};

export default function BillingSection() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [my, setMy] = useState(null);
  const [cycle, setCycle] = useState("monthly");
  const [busyKey, setBusyKey] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    try {
      const [p, m] = await Promise.all([endpoints.billingPlans(), endpoints.billingMe()]);
      setPlans(p.data || []);
      setMy(m.data || null);
    } catch (err) {
      console.error(err);
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const visiblePlans = useMemo(
    () => plans.filter(p => p.cycle === cycle).sort((a, b) => a.amount - b.amount),
    [plans, cycle]
  );

  const annualSavings = (tier) => {
    const m = plans.find(p => p.tier === tier && p.cycle === "monthly");
    const a = plans.find(p => p.tier === tier && p.cycle === "annual");
    if (!m || !a) return 0;
    return Math.round(m.amount * 12 - a.amount);
  };

  const handleSubscribe = async (plan) => {
    if (!window.Razorpay) {
      toast.error("Payment library not loaded - please refresh");
      return;
    }
    setBusyKey(plan.key);
    try {
      // Try subscription path first; auto-fallback to one-time order on 412
      let res;
      try {
        res = await endpoints.billingCreateSubscription(plan.key);
      } catch (err) {
        if (err?.response?.status === 412) {
          res = await endpoints.billingCreateOrder(plan.key);
        } else {
          throw err;
        }
      }
      const data = res.data;
      const isSub = data.mode === "subscription";

      const options = {
        key: data.key_id,
        name: "Fitnudge",
        description: `${plan.name} (${plan.cycle})`,
        image: undefined,
        theme: { color: "#10B981" },
        prefill: {
          name: user?.name || "",
          email: user?.email || "",
        },
        notes: { plan_key: plan.key },
        ...(isSub
          ? { subscription_id: data.subscription_id }
          : { order_id: data.order_id, amount: data.amount, currency: data.currency }
        ),
        handler: async (response) => {
          try {
            await endpoints.billingVerifyPayment({
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_subscription_id: response.razorpay_subscription_id || null,
              razorpay_order_id: response.razorpay_order_id || null,
              razorpay_signature: response.razorpay_signature,
              plan_key: plan.key,
            });
            toast.success(`${plan.name} activated`, { description: "Welcome to Fitnudge - all features unlocked." });
            reload();
          } catch (err) {
            toast.error("Payment received but verification failed - contact support");
          }
        },
        modal: {
          ondismiss: () => setBusyKey(null),
        },
      };
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (resp) => {
        toast.error(`Payment failed: ${resp?.error?.description || "Try again"}`);
        setBusyKey(null);
      });
      rzp.open();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start payment");
      setBusyKey(null);
    }
  };

  if (loading) {
    return <div className="text-sm text-muted-foreground py-8 text-center">Loading plans…</div>;
  }

  return (
    <section data-testid="billing-section" className="space-y-6">
      {/* Current plan banner */}
      {my?.status === "active" && (
        <div data-testid="current-plan-banner" className="rounded-xl border border-emerald-200 dark:border-emerald-900/40 bg-emerald-50 dark:bg-emerald-950/20 p-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-bold text-foreground">
                {(plans.find(p => p.key === my.plan_key)?.name) || "Active"} plan is live
              </p>
              <p className="text-xs text-muted-foreground/85">{my.plan_key}{my.current_period_end ? ` · renews ${new Date(my.current_period_end).toLocaleDateString()}` : ""}</p>
            </div>
          </div>
        </div>
      )}

      {/* Cycle toggle */}
      <div className="flex items-center justify-center">
        <div className="inline-flex bg-muted rounded-full p-1" data-testid="cycle-toggle">
          {["monthly", "annual"].map(c => (
            <button
              key={c}
              type="button"
              onClick={() => setCycle(c)}
              data-testid={`cycle-${c}`}
              className={`px-4 py-1.5 text-xs font-semibold rounded-full transition-colors ${
                cycle === c ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {c === "annual" ? (
                <>Annual · <span className="text-emerald-600">save 10%</span></>
              ) : "Monthly"}
            </button>
          ))}
        </div>
      </div>

      {/* Plan cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="plan-grid">
        {visiblePlans.map(plan => {
          const meta = TIER_META[plan.tier] || TIER_META.starter;
          const Icon = meta.icon;
          const isCurrent = my?.plan_key === plan.key && my?.status === "active";
          const isPopular = plan.popular;
          const savings = cycle === "annual" ? annualSavings(plan.tier) : 0;
          return (
            <div
              key={plan.key}
              data-testid={`plan-card-${plan.key}`}
              className={`relative rounded-2xl border bg-card p-5 flex flex-col ${
                isPopular ? `border-primary/60 ring-2 ${meta.ring}` : "border-border"
              }`}
            >
              {isPopular && (
                <span className="absolute -top-2.5 left-5 bg-primary text-primary-foreground text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md">
                  Most popular
                </span>
              )}
              <div className={`w-9 h-9 rounded-lg bg-muted/60 flex items-center justify-center mb-3 ${meta.color}`}>
                <Icon className="w-4 h-4" />
              </div>
              <p className="font-display text-base font-bold text-foreground">{plan.name}</p>
              <p className="text-xs text-muted-foreground/80 mb-3">
                {plan.members_limit === 0 ? "Unlimited members" : `Up to ${plan.members_limit} members`}
              </p>

              <div className="flex items-baseline gap-1 mb-1">
                <span className="font-display text-3xl font-bold tracking-tight text-foreground tabular-nums">
                  ₹{plan.amount.toLocaleString("en-IN")}
                </span>
                <span className="text-xs text-muted-foreground">/{plan.cycle === "annual" ? "yr" : "mo"}</span>
              </div>
              {savings > 0 && (
                <p className="text-[11px] text-emerald-600 font-medium mb-3">
                  Saves ₹{savings.toLocaleString("en-IN")} vs monthly
                </p>
              )}
              {savings === 0 && <div className="mb-3" />}

              <ul className="space-y-1.5 mb-5 flex-1">
                {plan.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-foreground/85">
                    <Check className="w-3 h-3 mt-0.5 text-emerald-500 shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              {isCurrent ? (
                <div className="text-center text-xs font-semibold text-emerald-700 dark:text-emerald-400 py-2.5 border border-emerald-300 dark:border-emerald-800 rounded-md bg-emerald-50 dark:bg-emerald-950/20">
                  Current plan
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => handleSubscribe(plan)}
                  disabled={busyKey === plan.key}
                  data-testid={`subscribe-${plan.key}`}
                  className={`w-full inline-flex items-center justify-center gap-1.5 py-2.5 rounded-md text-sm font-semibold transition-colors ${
                    isPopular
                      ? "bg-primary text-primary-foreground hover:opacity-90"
                      : "border border-border bg-card hover:bg-muted text-foreground"
                  } disabled:opacity-60`}
                >
                  {busyKey === plan.key ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                  {busyKey === plan.key ? "Opening checkout…" : "Subscribe"}
                </button>
              )}
            </div>
          );
        })}
      </div>

      <p className="text-[11px] text-center text-muted-foreground/70 leading-relaxed">
        Powered by Razorpay · cancel anytime · 7-day pro-rata refund window. UPI, Cards, Netbanking and Wallets supported.
      </p>
    </section>
  );
}
